import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Target, TrendingUp, Globe, Users, Shield, Zap } from 'lucide-react';

const InvestmentOverview = () => {
  const productPortfolio = [
    {
      name: "GoFella",
      description: "Digital platform connecting Australians with verified tradies and service providers",
      status: "Beta Development"
    },
    {
      name: "Vincotek Platform Services",
      description: "Custom software development and IT consulting for businesses",
      status: "Active Revenue"
    },
    {
      name: "Digital Innovation Labs",
      description: "Next-generation products in planning and early development phases",
      status: "Concept Stage"
    }
  ];

  const monetizationModel = [
    "Subscription fees for tradie premium listings on GoFella",
    "Value-added services (premium support, marketing tools)",
    "Custom software development and consulting revenue",
    "API licensing and integration services"
  ];

  const roadmilestones = [
    {
      period: "Q1 2025",
      milestone: "GoFella beta launch",
      description: "Initial platform release with core features"
    },
    {
      period: "Q2 2025", 
      milestone: "1,000+ verified tradies",
      description: "Expand tradie network across major Australian cities"
    },
    {
      period: "Q3 2025",
      milestone: "Premium features release", 
      description: "Advanced booking system and payment integration"
    },
    {
      period: "Q4 2025",
      milestone: "10,000+ active users",
      description: "Scale user base and establish market presence"
    },
    {
      period: "Q1 2026",
      milestone: "Regional expansion",
      description: "Expand beyond major cities to regional areas"
    }
  ];

  const marketOpportunity = [
    {
      icon: <Target className="h-6 w-6" />,
      title: "Large Target Market", 
      description: "Australian trade services market valued at $15+ billion annually"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Growing Digital Adoption",
      description: "Accelerated shift to digital platforms post-COVID across all demographics"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Trust Gap in Market",
      description: "Significant demand for verified, trustworthy service providers"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Fragmented Competition",
      description: "No dominant platform exists - opportunity for market leadership"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-vincotek-dark to-gray-900 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Investment Overview
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Discover the opportunity to invest in Vincotek, an early-stage technology company building practical digital products that solve real Australian problems.
            </p>
          </div>
        </div>
      </section>

      {/* About Vincotek Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              About Vincotek
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Vincotek is a Australia-based technology company with a mission to build practical, impactful technology products that improve how Australians connect, work, and live. Founded in 2025, we combine technical innovation with deep understanding of local market needs to create digital solutions that generate both strong returns and positive social impact. Our approach focuses on identifying underserved markets where technology can create significant value for users while building sustainable, scalable business models.
            </p>
          </div>
        </div>
      </section>

      {/* Product Portfolio Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Our Product Portfolio
            </h2>
            
            <div className="space-y-8">
              {productPortfolio.map((product, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                      <div>
                        <h3 className="text-2xl font-bold text-vincotek-dark mb-2">
                          {product.name}
                        </h3>
                        <span className="bg-vincotek-blue text-white px-3 py-1 rounded-full text-sm font-medium">
                          {product.status}
                        </span>
                      </div>
                      <div className="lg:col-span-2">
                        <p className="text-gray-600 leading-relaxed">
                          {product.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Market Opportunity
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-4xl mx-auto">
              The Australian trade services industry represents a significant opportunity for digital disruption. Traditional methods of finding and hiring tradies are inefficient, lack transparency, and fail to build lasting trust between customers and service providers.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {marketOpportunity.map((opportunity, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="text-vincotek-blue mb-4">
                      {opportunity.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-vincotek-dark mb-3">
                      {opportunity.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {opportunity.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Business Model Section */}
      <section className="py-20 bg-gradient-to-br from-vincotek-yellow/10 to-yellow-100/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Business Model (High-Level)
            </h2>
            
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-vincotek-dark mb-6">Revenue Streams</h3>
              <div className="space-y-4">
                {monetizationModel.map((model, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-vincotek-blue text-white rounded-full flex items-center justify-center">
                      <span className="font-bold text-sm">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {model}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Roadmap
            </h2>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 transform md:-translate-x-0.5 h-full w-0.5 bg-vincotek-blue"></div>
              
              <div className="space-y-12">
                {roadmilestones.map((milestone, index) => (
                  <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Timeline dot */}
                    <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-vincotek-yellow rounded-full border-4 border-white shadow-lg z-10"></div>
                    
                    {/* Content */}
                    <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 pl-12' : 'md:pl-12 pl-12 md:text-right'}`}>
                      <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                        <CardContent className="p-6">
                          <div className="text-vincotek-blue font-bold text-sm mb-2">
                            {milestone.period}
                          </div>
                          <h3 className="text-lg font-bold text-vincotek-dark mb-3">
                            {milestone.milestone}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {milestone.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Investors Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Contact for Investors
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Interested in learning more about investment opportunities with Vincotek? We'd love to discuss how you can be part of our journey to revolutionize digital services in Australia.
          </p>
          <div className="space-y-4">
            <p className="text-lg text-gray-300">
              For investor enquiries, please reach out through our contact form or email us directly.
            </p>
            <Link to="/contact">
              <Button className="mt-4 bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Contact Us for Investment Opportunities
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentOverview;